<template>
  <main class="home-page">
    <section v-if="featured" class="featured-section iso-container">
      <h2 class="iso-section-title">{{ $t('article.featured') }}</h2>
      <div class="featured-card iso-card iso-depth-side" @click="goArticle(featured.id)">
        <div class="featured-cover">
          <img
            v-if="featured.cover_image"
            :src="featured.cover_image"
            :alt="featured.title"
            loading="lazy"
            class="featured-img"
          />
          <div v-else class="featured-placeholder">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        </div>
        <div class="featured-body">
          <span v-if="featured.category" class="iso-tag">{{ featured.category }}</span>
          <h3 class="featured-title">{{ featured.title }}</h3>
          <p v-if="featured.summary" class="featured-summary">{{ featured.summary }}</p>
          <span v-if="featured.published_at" class="featured-date">{{ featured.published_at }}</span>
        </div>
      </div>
    </section>

    <section class="articles-section iso-container">
      <h2 class="iso-section-title">{{ $t('article.latest') }}</h2>
      <div v-if="articles.length" class="iso-grid">
        <IsometricCard
          v-for="article in articles"
          :key="article.id"
          :id="article.id"
          :title="article.title"
          :summary="article.summary"
          :cover-image="article.cover_image"
          :date="article.published_at"
          :category="article.category"
          :slug="article.slug"
        />
      </div>
      <div v-else-if="!loading" class="empty-state">
        <p>{{ $t('article.noArticles') }}</p>
      </div>
      <div v-if="loading" class="iso-loading">{{ $t('common.loading') }}</div>
      <div v-if="hasMore && !loading" class="load-more">
        <button class="iso-btn" @click="loadMore">{{ $t('common.loadMore') }}</button>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { request } from '@/api'
import IsometricCard from '@/components/IsometricCard.vue'

interface Article {
  id: number
  title: string
  slug: string
  summary?: string
  content?: string
  category?: string
  cover_image?: string
  source?: string
  published_at?: string
  is_featured?: number
}

const router = useRouter()

const articles = ref<Article[]>([])
const featured = ref<Article | null>(null)
const loading = ref(false)
const hasMore = ref(true)
const page = ref(1)
const pageSize = 12

function goArticle(id: number) {
  router.push(`/article/${id}`)
}

async function fetchArticles(append = false) {
  if (loading.value) return
  loading.value = true
  try {
    const res = await request<{ data: Article[] }>(`/news?limit=${pageSize}&offset=${(page.value - 1) * pageSize}`)
    const list = res.data || []
    if (append) {
      articles.value.push(...list)
    } else {
      articles.value = list
    }
    hasMore.value = list.length >= pageSize
    const featItem = list.find(a => a.is_featured === 1)
    if (featItem) featured.value = featItem
  } catch {
    hasMore.value = false
  } finally {
    loading.value = false
  }
}

function loadMore() {
  page.value++
  fetchArticles(true)
}

onMounted(() => {
  fetchArticles()
})
</script>

<style scoped>
.home-page {
  padding-top: calc(var(--header-height) + 32px);
  padding-bottom: 48px;
}

.featured-section {
  margin-bottom: 48px;
}

.featured-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  cursor: pointer;
  overflow: hidden;
}

.featured-cover {
  height: 100%;
  min-height: 280px;
  overflow: hidden;
}

.featured-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}

.featured-card:hover .featured-img {
  transform: scale(1.05);
}

.featured-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.06) 0%, rgba(0, 212, 255, 0.02) 100%);
  color: var(--iso-cyan-dim);
}

.featured-body {
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
}

.featured-title {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  line-height: 1.4;
  transition: color 0.2s;
}

.featured-card:hover .featured-title {
  color: var(--iso-cyan);
}

.featured-summary {
  font-size: 14px;
  color: var(--iso-text-dim);
  line-height: 1.7;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.featured-date {
  font-size: 12px;
  color: var(--iso-text-dim);
}

.articles-section {
  margin-bottom: 32px;
}

.empty-state {
  text-align: center;
  padding: 64px 24px;
  color: var(--iso-text-dim);
}

.load-more {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

@media (max-width: 768px) {
  .featured-card {
    grid-template-columns: 1fr;
  }

  .featured-cover {
    min-height: 200px;
  }

  .featured-body {
    padding: 20px;
  }

  .featured-title {
    font-size: 18px;
  }
}
</style>
